import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown, Star, MapPin } from "lucide-react";
import Particles from "../Particles";
import { MagneticButton } from "../MagneticButton";
import { TextReveal } from "../Reveal";
import { CONTACT } from "../../lib/data";

const HERO_IMG = "https://images.unsplash.com/photo-1655837425341-b59fc33c5898?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const yText = useTransform(scrollYProgress, [0, 1], ["0%", "120%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  // mouse parallax
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 20 });
  const sy = useSpring(my, { stiffness: 60, damping: 20 });
  const onMove = (e) => {
    const { innerWidth, innerHeight } = window;
    mx.set((e.clientX / innerWidth - 0.5) * 30);
    my.set((e.clientY / innerHeight - 0.5) * 30);
  };

  const go = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="hero"
      ref={ref}
      onMouseMove={onMove}
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden bg-ink grain"
    >
      <motion.div className="absolute inset-0" style={{ y: yImg }}>
        <motion.img
          src={HERO_IMG}
          alt="Luxury car in dark studio"
          className="h-[120%] w-full object-cover"
          style={{ x: sx, scale: 1.08 }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/80 to-ink/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/60" />
      </motion.div>

      <Particles density={45} className="z-[1]" />

      <motion.div
        style={{ y: yText, opacity }}
        className="relative z-10 mx-auto w-full max-w-7xl px-6 md:px-10"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.7 }}
          className="mb-6 inline-flex items-center gap-2 rounded-full glass px-4 py-2"
        >
          <span className="flex items-center gap-1 text-gold">
            {[...Array(5)].map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-gold" strokeWidth={0} />)}
          </span>
          <span className="text-sm text-white/80">{CONTACT.rating} · {CONTACT.reviews} reviews</span>
          <span className="mx-1 h-3 w-px bg-white/20" />
          <span className="flex items-center gap-1 text-sm text-white/60"><MapPin className="h-3.5 w-3.5" strokeWidth={1.5} />{CONTACT.area}</span>
        </motion.div>

        <h1 className="font-display text-5xl font-semibold leading-[0.95] tracking-tighter sm:text-6xl lg:text-8xl">
          <TextReveal text="The Luxury" delay={0.5} mode="mount" /> <br className="hidden sm:block" />
          <span className="text-gradient-gold"><TextReveal text="of Time." delay={0.7} mode="mount" /></span> <br />
          <TextReveal text="We Come To You." delay={0.9} mode="mount" />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.7 }}
          className="mt-8 max-w-xl text-base text-white/60 md:text-lg"
        >
          Premium mobile oil changes in {CONTACT.area}. No shops, no waiting rooms —
          our team arrives fully prepared and has you driving in about 20 minutes.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.7 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <MagneticButton
            data-testid="hero-cta-button"
            onClick={() => go("#contact")}
            className="rounded-full bg-gold px-8 py-4 font-semibold text-ink transition-colors hover:bg-gold-hover gold-glow"
          >
            Book Your Oil Change
          </MagneticButton>
          <MagneticButton
            data-testid="hero-services-button"
            onClick={() => go("#services")}
            className="rounded-full border border-white/15 px-8 py-4 font-medium text-white transition-colors hover:border-gold hover:text-gold"
          >
            Explore Services
          </MagneticButton>
        </motion.div>
      </motion.div>

      <motion.button
        onClick={() => go("#about")}
        style={{ opacity }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 flex flex-col items-center gap-2 text-white/40"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <span className="text-xs uppercase tracking-[0.3em]">Scroll</span>
        <ArrowDown className="h-4 w-4" strokeWidth={1.5} />
      </motion.button>
    </section>
  );
}
